import React, { useEffect, useState } from "react";
import {
  Building2,
  ShieldAlert,
  Landmark,
  Sliders,
  ClipboardCheck,
  BarChart3,
  FlaskConical,
  ScrollText,
  Bell,
  TrendingUp,
  TrendingDown,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Map,
} from "lucide-react";
import RadialNavigator from "../../components/navigation/RadialNavigator";
import dashboardService, {
  OrgKpis,
  RiskSummary,
  ControlHealth,
  ComplianceSummary,
  GovernanceSummary,
} from "../../services/dashboardService";

interface AdminDashboardProps {
  activeMenu: string;
  onNavigate?: (menuId: string) => void;
}

/* ── Status row — neutral ── */
type DS = "ok" | "warn" | "err";
const SRow: React.FC<{ label: string; s: DS }> = ({ label, s }) => {
  const cfg: Record<DS, { icon: React.ReactNode; cls: string }> = {
    ok: { icon: <CheckCircle2 size={11} />, cls: "text-emerald-400" },
    warn: { icon: <AlertCircle size={11} />, cls: "text-amber-400" },
    err: { icon: <XCircle size={11} />, cls: "text-rose-400" },
  };
  return (
    <div className="flex items-center gap-2 py-[3px]">
      <span className={cfg[s].cls}>{cfg[s].icon}</span>
      <span className="text-[11px] text-gray-500 leading-snug">{label}</span>
    </div>
  );
};

/* ── Muted card ── */
const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div
    className="flex flex-col overflow-hidden rounded-xl"
    style={{
      background: "#fff",
      border: "1px solid #eaecf0",
      boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    }}
  >
    {children}
  </div>
);

const MockBadge = () => (
  <span className="status-badge sm warning">
    MOCK
  </span>
);

/* ── Card header — colorful icon + title ──
   `mock` defaults to true so every still-placeholder widget keeps the MOCK
   badge; real (DB-wired) widgets pass mock={false}. */
const CHead: React.FC<{
  icon: React.ReactNode;
  title: string;
  color: string;
  badge?: React.ReactNode;
  mock?: boolean;
}> = ({ icon, title, color, badge, mock = true }) => (
  <div className="flex items-center justify-between px-3 pt-2.5 pb-1.5">
    <div className="flex items-center gap-1.5">
      <span className={color}>{icon}</span>
      <span
        className={`text-[9.5px] font-bold uppercase tracking-widest ${color}`}
      >
        {title}
      </span>
    </div>
    <div className="flex items-center gap-1">
      {badge}
      {mock && <MockBadge />}
    </div>
  </div>
);

/* ── Colored badge ── */
type BadgeVariant = "success" | "danger" | "warning" | "info" | "neutral";
const Badge: React.FC<{ label: string; variant: BadgeVariant }> = ({
  label,
  variant,
}) => (
  <span className={`status-badge xs ${variant}`}>
    {label}
  </span>
);

/* ── Band bar list (shared by Risk Summary & Control Health) ── */
type Band = { l: string; n: number; bar: string };
const BandBars: React.FC<{
  bands: Band[];
  loading: boolean;
  emptyLabel: string;
  testid: string;
  footer?: string;
}> = ({ bands, loading, emptyLabel, testid, footer }) => {
  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center px-3 pb-3">
        <span className="text-[10px] text-gray-400">Loading…</span>
      </div>
    );
  }
  const total = bands.reduce((s, b) => s + b.n, 0);
  if (total === 0) {
    return (
      <div className="flex-1 flex items-center justify-center px-3 pb-3">
        <span className="text-[10px] text-gray-400">{emptyLabel}</span>
      </div>
    );
  }
  const max = Math.max(1, ...bands.map((b) => b.n));
  return (
    <div
      className="flex-1 flex flex-col justify-center gap-1.5 px-3 pb-2"
      data-testid={testid}
    >
      {bands.map(({ l, n, bar }) => (
        <div key={l} className="flex items-center gap-2">
          <span className="text-[9.5px] font-semibold w-14 text-right text-gray-500">
            {l}
          </span>
          <div className="flex-1 bg-gray-100 rounded-full h-1.5 overflow-hidden">
            <div
              className="h-1.5 rounded-full"
              style={{ width: `${(n / max) * 100}%`, background: bar }}
            />
          </div>
          <span
            className="text-[10px] font-bold w-6 text-center text-gray-600"
            data-testid={`${testid}-${l.toLowerCase()}`}
          >
            {n}
          </span>
        </div>
      ))}
      {footer && (
        <div
          className="text-[8.5px] text-gray-400 text-center pt-0.5"
          data-testid={`${testid}-total`}
        >
          {footer}
        </div>
      )}
    </div>
  );
};

/* ══ AdminDashboard ══ */
const AdminDashboard: React.FC<AdminDashboardProps> = ({
  activeMenu,
  onNavigate,
}) => {
  const [org, setOrg] = useState<OrgKpis | null>(null);
  const [risk, setRisk] = useState<RiskSummary | null>(null);
  const [control, setControl] = useState<ControlHealth | null>(null);
  const [compliance, setCompliance] = useState<ComplianceSummary | null>(null);
  const [governance, setGovernance] = useState<GovernanceSummary | null>(null);

  useEffect(() => {
    let alive = true;
    dashboardService
      .orgKpis()
      .then((d) => alive && setOrg(d))
      .catch(() => {});
    dashboardService
      .riskSummary()
      .then((d) => alive && setRisk(d))
      .catch(() => {});
    dashboardService
      .controlHealth()
      .then((d) => alive && setControl(d))
      .catch(() => {});
    dashboardService
      .complianceSummary()
      .then((d) => alive && setCompliance(d))
      .catch(() => {});
    dashboardService
      .governanceSummary()
      .then((d) => alive && setGovernance(d))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const orgStat = (v: number | undefined) =>
    org === null ? "…" : String(v ?? 0);

  // Unscored = risks/controls with no usable score yet. Showing it makes the
  // bands reconcile to the total: High + Medium + Low + Unscored = Total.
  const riskUnscored = risk ? Math.max(0, risk.total_risks - risk.scored) : 0;
  const controlUnscored = control
    ? Math.max(0, control.total_controls - control.scored)
    : 0;

  const riskBands: Band[] = risk
    ? [
        { l: "High", n: risk.high, bar: "#f87171" },
        { l: "Medium", n: risk.medium, bar: "#fbbf24" },
        { l: "Low", n: risk.low, bar: "#34d399" },
        { l: "Unscored", n: riskUnscored, bar: "#cbd5e1" },
      ]
    : [];

  // Control health is the inverse of risk: High band = healthy (green).
  const controlBands: Band[] = control
    ? [
        { l: "High", n: control.high, bar: "#34d399" },
        { l: "Medium", n: control.medium, bar: "#fbbf24" },
        { l: "Low", n: control.low, bar: "#f87171" },
        { l: "Unscored", n: controlUnscored, bar: "#cbd5e1" },
      ]
    : [];

  return (
    <div
      className="grid gap-3 p-2 overflow-hidden scroll-container"
      style={{
        height: "calc(100vh - 140px)",
        gridTemplateColumns: "1.9fr 1fr 1fr 1fr",
        gridTemplateRows: "1fr 1fr 1fr",
        background: "#f7f8fc",
      }}
    >
      {/* ══ COL 1 – Site Map hero panel ══ */}
      <div
        className="flex flex-col overflow-hidden rounded-2xl"
        style={{
          gridRow: "span 3",
          background:
            "linear-gradient(160deg, #faf9ff 0%, #f3f0ff 60%, #ede9fe 100%)",
          border: "1.5px solid #ddd6fe",
          boxShadow:
            "0 4px 24px rgba(109,40,217,0.10), 0 1px 4px rgba(109,40,217,0.06)",
        }}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 pt-3 pb-2 flex-shrink-0">
          <div
            className="flex items-center justify-center rounded-lg"
            style={{
              width: 26,
              height: 26,
              background: "linear-gradient(135deg,#7c3aed,#a855f7)",
            }}
          >
            <Map size={13} color="#fff" />
          </div>
          <span className="text-[11px] font-bold text-violet-700 uppercase tracking-widest">
            Site Map
          </span>
        </div>
        {/* Divider */}
        <div
          style={{
            height: 1,
            background: "linear-gradient(to right, #ddd6fe, transparent)",
            margin: "0 16px",
          }}
        />
        {/* Navigator */}
        <div
          className="flex-1 flex items-center justify-center overflow-hidden"
          style={{ minHeight: 0 }}
        >
          <RadialNavigator
            className="compact expanded"
            radius={185}
            activeId={activeMenu === "dashboard" ? "organisation" : activeMenu}
            onSelect={(id) => {
              onNavigate?.(id);
            }}
          />
        </div>
      </div>

      {/* ══ ROW 1: Organisation | Risk Summary | Governance ══ */}

      {/* Organisation — REAL (org-kpis) */}
      <Card>
        <CHead
          icon={<Building2 size={11} />}
          title="Organisation"
          color="text-blue-500"
          mock={false}
        />
        <div
          className="flex-1 flex items-center justify-around px-3 pb-3 gap-1"
          data-testid="org-kpis"
        >
          {[
            [orgStat(org?.departments), "Depts"],
            [orgStat(org?.people), "People"],
            [orgStat(org?.sops), "SOPs"],
          ].map(([v, l]) => (
            <div key={l} className="flex flex-col items-center">
              <span className="text-xl font-black text-gray-700">{v}</span>
              <span className="text-[9px] text-gray-400 font-medium uppercase tracking-wide">
                {l}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Risk Summary — REAL (risk-summary) */}
      <Card>
        <CHead
          icon={<ShieldAlert size={11} />}
          title="Risk Summary"
          color="text-rose-500"
          mock={false}
          badge={
            risk && risk.scored > 0 ? (
              <Badge label={`${risk.high} HIGH`} variant="danger" />
            ) : undefined
          }
        />
        <BandBars
          bands={riskBands}
          loading={risk === null}
          emptyLabel="No risks yet"
          testid="risk-summary-bands"
          footer={risk ? `${risk.total_risks} risks total` : undefined}
        />
      </Card>

      {/* Governance — REAL (governance-summary: tasks & meetings) */}
      <Card>
        <CHead
          icon={<Landmark size={11} />}
          title="Governance"
          color="text-violet-500"
          mock={false}
          badge={
            governance ? (
              governance.tasks_overdue + governance.meetings_overdue > 0 ? (
                <Badge
                  label={`${governance.tasks_overdue + governance.meetings_overdue} OVERDUE`}
                  variant="danger"
                />
              ) : (
                <Badge label="ON TRACK" variant="success" />
              )
            ) : undefined
          }
        />
        {governance === null ? (
          <div className="flex-1 flex items-center justify-center px-3 pb-3">
            <span className="text-[10px] text-gray-400">Loading…</span>
          </div>
        ) : (
          <div
            className="flex-1 flex flex-col justify-center px-3 pb-3 gap-2"
            data-testid="governance-summary"
          >
            {[
              {
                key: "tasks",
                group: "Tasks",
                up: governance.tasks_upcoming,
                over: governance.tasks_overdue,
              },
              {
                key: "meetings",
                group: "Meetings",
                up: governance.meetings_upcoming,
                over: governance.meetings_overdue,
              },
            ].map(({ key, group, up, over }) => (
              <div key={key}>
                <div className="text-[8.5px] font-bold uppercase tracking-wide text-gray-400 mb-0.5">
                  {group}
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Clock size={10} className="text-amber-500" />
                    <span className="text-[10px] text-gray-500">Upcoming</span>
                    <span
                      className="text-[11px] font-bold text-amber-600"
                      data-testid={`governance-summary-${key}-upcoming`}
                    >
                      {up}
                    </span>
                  </div>
                  <div className="flex items-center gap-1">
                    <AlertCircle size={10} className="text-rose-500" />
                    <span className="text-[10px] text-gray-500">Overdue</span>
                    <span
                      className="text-[11px] font-bold text-rose-600"
                      data-testid={`governance-summary-${key}-overdue`}
                    >
                      {over}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* ══ ROW 2: Controls Health | Compliance | KPI ══ */}

      {/* Controls Health — REAL (control-health) */}
      <Card>
        <CHead
          icon={<Sliders size={11} />}
          title="Controls Health"
          color="text-indigo-500"
          mock={false}
          badge={
            control && control.scored > 0 ? (
              <Badge
                label={`${control.healthy_pct}% HEALTHY`}
                variant="success"
              />
            ) : undefined
          }
        />
        <BandBars
          bands={controlBands}
          loading={control === null}
          emptyLabel="No controls yet"
          testid="control-health-bands"
          footer={control ? `${control.total_controls} controls total` : undefined}
        />
      </Card>

      {/* Compliance — REAL (compliance-summary) */}
      <Card>
        <CHead
          icon={<ClipboardCheck size={11} />}
          title="Compliance"
          color="text-teal-500"
          mock={false}
          badge={
            compliance ? (
              compliance.overdue > 0 ? (
                <Badge label={`${compliance.overdue} OVERDUE`} variant="danger" />
              ) : (
                <Badge label="ON TRACK" variant="success" />
              )
            ) : undefined
          }
        />
        {compliance === null ? (
          <div className="flex-1 flex items-center justify-center px-3 pb-3">
            <span className="text-[10px] text-gray-400">Loading…</span>
          </div>
        ) : (
          <div
            className="flex-1 flex flex-col justify-center px-3 pb-3 gap-1"
            data-testid="compliance-summary"
          >
            {[
              { l: "Obligations", v: compliance.total_obligations, c: "text-gray-700", t: "obligations" },
              { l: "Upcoming (7d)", v: compliance.upcoming, c: "text-amber-600", t: "upcoming" },
              { l: "Overdue (backlog)", v: compliance.overdue, c: "text-rose-600", t: "overdue" },
              { l: "Litigations pending", v: compliance.litigations_pending, c: "text-indigo-600", t: "litigations" },
            ].map(({ l, v, c, t }) => (
              <div key={t} className="flex items-center justify-between">
                <span className="text-[11px] text-gray-500 leading-snug">{l}</span>
                <span
                  className={`text-[13px] font-bold ${c}`}
                  data-testid={`compliance-summary-${t}`}
                >
                  {v}
                </span>
              </div>
            ))}
          </div>
        )}
      </Card>

      {/* KPI Snapshot */}
      <Card>
        <CHead
          icon={<BarChart3 size={11} />}
          title="KPI Snapshot"
          color="text-emerald-500"
        />
        <div className="flex-1 flex items-center gap-2 px-3 pb-3">
          {[
            {
              v: "94%",
              l: "On-Time",
              bg: "#f0fdf4",
              val: "#16a34a",
              icon: <TrendingUp size={9} color="#16a34a" />,
            },
            {
              v: "12",
              l: "Open",
              bg: "#eff6ff",
              val: "#2563eb",
              icon: <TrendingDown size={9} color="#2563eb" />,
            },
            {
              v: "4",
              l: "Overdue",
              bg: "#fff7ed",
              val: "#ea580c",
              icon: <AlertCircle size={9} color="#ea580c" />,
            },
          ].map(({ v, l, bg, val, icon }) => (
            <div
              key={l}
              className="flex-1 flex flex-col items-center justify-center rounded-lg py-2"
              style={{ background: bg }}
            >
              <span
                className="text-base font-black leading-tight"
                style={{ color: val }}
              >
                {v}
              </span>
              <div className="flex items-center gap-0.5 mt-0.5">
                {icon}
                <span
                  className="text-[8.5px] font-semibold uppercase tracking-wide"
                  style={{ color: val }}
                >
                  {l}
                </span>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* ══ ROW 3: Testing | Audit Log | Notifications ══ */}

      {/* Testing Progress */}
      <Card>
        <CHead
          icon={<FlaskConical size={11} />}
          title="Testing Progress"
          color="text-purple-500"
          badge={<Badge label="Q3 ACTIVE" variant="info" />}
        />
        <div className="flex-1 flex flex-col justify-center px-3 pb-3 gap-1.5">
          <div>
            <div className="flex justify-between text-[9.5px] mb-1">
              <span className="text-gray-400 font-medium">
                Overall completion
              </span>
              <span className="font-bold text-gray-600">78%</span>
            </div>
            <div className="bg-gray-100 rounded-full h-1.5 overflow-hidden">
              <div
                className="h-1.5 rounded-full"
                style={{ width: "78%", background: "#a78bfa" }}
              />
            </div>
          </div>
          <SRow s="warn" label="Evidence Pending: 5 items" />
          <SRow s="ok" label="Deficiencies Open: 2 minor" />
        </div>
      </Card>

      {/* Audit Log */}
      <Card>
        <CHead
          icon={<ScrollText size={11} />}
          title="Audit Log"
          color="text-sky-500"
          badge={<Badge label="LIVE" variant="info" />}
        />
        <div className="flex-1 flex flex-col justify-center px-3 pb-3 gap-0.5">
          {[
            ["User role updated", "2m ago"],
            ["Policy ACK recorded", "15m ago"],
            ["New incident filed", "1h ago"],
          ].map(([msg, t]) => (
            <div key={msg} className="flex items-center justify-between py-[3px]">
              <div className="flex items-center gap-1.5">
                <span className="w-1 h-1 rounded-full bg-gray-300 flex-shrink-0" />
                <span className="text-[11px] text-gray-500">{msg}</span>
              </div>
              <span className="text-[9.5px] text-gray-400 flex-shrink-0 ml-2">
                {t}
              </span>
            </div>
          ))}
        </div>
      </Card>

      {/* Notifications */}
      <Card>
        <CHead
          icon={<Bell size={11} />}
          title="Notifications"
          color="text-amber-500"
          badge={<Badge label="1 URGENT" variant="warning" />}
        />
        <div className="flex-1 flex flex-col justify-center px-3 pb-3 gap-0.5">
          <SRow s="warn" label="Policy Review Overdue" />
          <SRow s="ok" label="New User Added" />
          <SRow s="ok" label="Control Test Passed" />
        </div>
      </Card>
    </div>
  );
};

export default AdminDashboard;
